// [claude-code 2026-04-25] Consensus + chamber-confidence numerals now use DigitGroup
//   (solvys-transitions number pop-in) so the percentages cascade in on every verdict refresh.
// [claude-code 2026-04-24] S35-T3: standalone Arbitrum verdict card — consensus, confidence, digest, dissent
// [claude-code 2026-05-03] S57: embedded mode drops inner card border for chamber layout.
import { NothingFuse } from "../shared/NothingFuse";
import { DigitGroup } from "../shared/DigitGroup";
import { FadingRuler } from "../shared/FadingRuler";
import { AgenticFeedbackControls } from "../shared/AgenticFeedbackControls";
import { DissentBadge } from "./DissentBadge";
import { StreamdownChat } from "../chat/slots";
import { cleanArbitrumDigestText } from "./digest-text";
import type { ArbitrumVerdict } from "./types";

interface VerdictCardProps {
  verdict: ArbitrumVerdict;
  compact?: boolean;
  embedded?: boolean;
  className?: string;
}

export function VerdictCard({
  verdict,
  compact = false,
  embedded = false,
  className,
}: VerdictCardProps) {
  const {
    consensus_probability,
    confidence,
    digest_text,
    dissent,
    created_at,
    trigger,
  } = verdict;
  const consensusScore = Math.max(0, Math.min(10, consensus_probability * 10));
  const confidenceScore = Math.max(0, Math.min(10, confidence * 10));
  const cleanedDigest = cleanArbitrumDigestText(digest_text);
  const surfaceClass = embedded
    ? "bg-transparent py-2"
    : `bg-[var(--fintheon-bg)] border border-[var(--fintheon-accent)]/30 ${
        compact ? "p-3" : "p-4"
      }`;

  return (
    <div className={`relative ${surfaceClass} ${className ?? ""}`}>
      {embedded && <FadingRuler className="mb-2" />}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <DigitGroup
            value={consensusScore.toFixed(1)}
            className="text-[var(--fintheon-accent)] leading-none"
            style={{
              fontFamily: "Doto, ui-monospace, monospace",
              fontSize: compact ? 28 : 40,
            }}
          />
          <span className="text-[10px] uppercase tracking-wider text-[var(--fintheon-text)]/60">
            consensus score
          </span>
        </div>
        {dissent && <DissentBadge dissent={dissent} />}
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-wider text-[var(--fintheon-text)]/50">
            chamber confidence
          </span>
          <DigitGroup
            value={confidenceScore.toFixed(1)}
            className="text-[var(--fintheon-text)]/80 text-xs"
            style={{ fontFamily: "Doto, ui-monospace, monospace" }}
          />
        </div>
        <NothingFuse
          value={confidence}
          color="var(--fintheon-accent)"
          thickness={3}
          segments={10}
        />
      </div>

      <div
        className={`mt-3 prose prose-invert prose-sm max-w-none
          [&_p]:text-[var(--fintheon-text)]/85 [&_p]:leading-relaxed [&_p]:my-1
          [&_li]:text-[var(--fintheon-text)]/75 [&_ul]:my-1 [&_ul]:pl-4
          [&_strong]:text-[var(--fintheon-accent)] [&_strong]:font-medium
          [&_em]:text-[var(--fintheon-text)]/55 [&_em]:not-italic
          [&_hr]:border-[var(--fintheon-accent)]/15 [&_hr]:my-2
          ${compact ? "text-[11px]" : "text-sm"}
          ${embedded ? "max-h-[18rem] overflow-y-auto pr-2" : ""}`}
      >
        <StreamdownChat
          content={cleanedDigest}
          className={`${compact ? "text-[12px]" : "text-[13px]"} leading-[1.6]`}
        />
      </div>

      <div className="mt-3 flex items-center justify-between text-[10px] text-[var(--fintheon-text)]/40">
        <span className="tabular-nums">
          {new Date(created_at).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
          {" · "}
          {new Date(created_at).toLocaleDateString([], {
            month: "short",
            day: "numeric",
          })}
        </span>
        {trigger && <span className="uppercase tracking-wider">{trigger}</span>}
      </div>
      {embedded && <FadingRuler className="mt-2" />}
      <AgenticFeedbackControls surface="arbitrum-chamber" itemId={verdict.id} />
    </div>
  );
}
